// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyContactStatus } from "@/types";
import { formatDate } from "@/utils/format";
import type { ContactPopupDetails } from "./types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
    details?: ContactPopupDetails;
};

// --- Constants ----------------------------------------------------------------------------------

const STATUSES: TyContactStatus[] = ["new", "read", "replied"];

// --- Logic --------------------------------------------------------------------------------------

/**
 * The full message behind a table row, with the two contact channels ready to be copied.
 *
 * The table can only ever show the first line of a message, and answering means getting the address
 * or the number out of the browser and into whatever you reply with — so copying is a button here
 * rather than a selection the admin has to make by hand.
 */
export default function ContactPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const [copied, setCopied] = useState<string | null>(null);
    const [status, setStatus] = useState<TyContactStatus | null>(null);
    const [saving, setSaving] = useState(false);

    if (!details) return null;

    const { contact, onStatus, onFilter } = details;
    // The popup owns the status only after a change here, so a reopened row shows the saved one.
    const current = status ?? contact.status;

    // --- Handlers --------------------------------------------------------------------------------

    const handleCopy = async (value: string, key: string) => {
        await navigator.clipboard.writeText(value);
        setCopied(key);
        setTimeout(() => setCopied((prev) => (prev === key ? null : prev)), 1800);
    };

    const handleStatus = async (next: TyContactStatus) => {
        if (!onStatus || next === current || saving) return;
        setSaving(true);
        const ok = await onStatus(next);
        setSaving(false);
        if (ok !== false) setStatus(next);
    };

    // --- Render ----------------------------------------------------------------------------------

    const channels = (
        [
            { key: "email", label: t.contacts.colEmail, value: contact.email, repeats: contact.repeats?.email ?? 0 },
            { key: "phone", label: t.contacts.colPhone, value: contact.phone, repeats: contact.repeats?.phone ?? 0 },
        ] as const
    ).filter((channel) => channel.value);

    return (
        <div className="contact-popup">
            <div className="contact-popup-head">
                <div className="contact-popup-heading">
                    <p className="contact-popup-name">{contact.name}</p>
                    <span className="contact-popup-date">{formatDate(contact.createdAt, lang)}</span>
                </div>
                <button type="button" className="contact-popup-close" onClick={onClose} aria-label={t.table.deleteCancel}>
                    <img src="/icons/close-icon.svg" alt="" width={14} height={14} />
                </button>
            </div>

            <div className="contact-popup-body">
                <p className="contact-popup-label">{t.contacts.colMessage}</p>
                <p className="contact-popup-message">{contact.message}</p>

                {channels.length > 0 && (
                    <div className="contact-popup-channels">
                        {channels.map((channel) => (
                            <div key={channel.key} className="contact-popup-channel">
                                <span className="contact-popup-channel-label">{channel.label}</span>
                                <span className="contact-popup-channel-value" dir="ltr">
                                    {channel.value}
                                </span>
                                {/* Only when there is more than one: a "1" on every row would be
                                    noise, and the number is here precisely to say "again". */}
                                {channel.repeats > 1 && (
                                    <button
                                        type="button"
                                        className="contact-popup-repeats"
                                        onClick={() => onFilter(channel.key, channel.value as string)}
                                        title={t.contacts.repeatsHint}
                                    >
                                        {t.contacts.repeats.replace("{count}", String(channel.repeats))}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="contact-popup-copy"
                                    data-done={copied === channel.key}
                                    onClick={() => handleCopy(channel.value as string, channel.key)}
                                >
                                    {copied === channel.key ? t.contacts.copied : t.contacts.copy}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="contact-popup-foot">
                <span className="contact-popup-label">{t.cols.status}</span>
                <div className="contact-popup-statuses">
                    {STATUSES.map((entry) => (
                        <button
                            key={entry}
                            type="button"
                            className="contact-popup-status"
                            data-on={entry === current}
                            onClick={() => handleStatus(entry)}
                            disabled={!onStatus || saving}
                        >
                            {t.enums.contactStatus[entry]}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
